import { FolderOpen } from "lucide-react";
import React from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/notifications/NotificationBell";
import { Home, Users, Megaphone, BarChart3, Settings, LogOut, FileText, Wrench } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { listBuildings } from "@/api/buildings";
import { clearAccessToken } from "@/api/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { useDevShortcuts } from "@/hooks/useDevShortcuts";

/** ------------------------------------------------------------
 * Polling support (context + hook + helper)
 * -----------------------------------------------------------*/
type PollingState = {
  enabled: boolean;
  intervalMs: number;
  toggle: () => void;
  cycleInterval: () => void; // 15s -> 30s -> 60s -> 15s
};

const PollingContext = React.createContext<PollingState | null>(null);

export function usePolling() {
  const ctx = React.useContext(PollingContext);
  if (!ctx) throw new Error("usePolling must be used within <HoaAdminLayout />");
  return ctx;
}

export function getPollingQueryOpts(enabled: boolean, intervalMs: number) {
  return {
    refetchInterval: enabled ? intervalMs : false,
    refetchOnWindowFocus: enabled,
  } as const;
}

/** ------------------------------------------------------------
 * Which building the admin is currently looking at
 *
 * This used to be local state on the dashboard, so nothing outside that one
 * page could tell which building was picked -- including the header, which is
 * where an admin managing several of them most needs to see it.
 *
 * Persisted in sessionStorage so the answer survives navigating between
 * tabs. It is a view preference, not identity: sessionStorage is already
 * cleared on logout, so it cannot leak into the next admin's session.
 *
 * "" means all buildings.
 * -----------------------------------------------------------*/
const SELECTED_BUILDING_KEY = "hoa_selected_building";

type SelectedBuildingState = {
  buildingId: string;
  setBuildingId: (id: string) => void;
};

const SelectedBuildingContext = React.createContext<SelectedBuildingState | null>(null);

export function useSelectedBuilding() {
  const ctx = React.useContext(SelectedBuildingContext);
  if (!ctx) throw new Error("useSelectedBuilding must be used within <HoaAdminLayout />");
  return ctx;
}

/** ------------------------------------------------------------
 * Layout
 * -----------------------------------------------------------*/
const PRIMARY = "#1F3A93"; // brand blue
const BG = "#F5F6FA";

export default function HoaAdminLayout() {
  const [enabled, setEnabled] = React.useState(true);
  const [intervalMs, setIntervalMs] = React.useState(30000);
  const toggle = React.useCallback(() => setEnabled((s) => !s), []);
  const cycleInterval = React.useCallback(
    () => setIntervalMs((cur) => (cur === 15000 ? 30000 : cur === 30000 ? 60000 : 15000)),
    []
  );
  const pollingValue = React.useMemo<PollingState>(
    () => ({ enabled, intervalMs, toggle, cycleInterval }),
    [enabled, intervalMs, toggle, cycleInterval]
  );

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { t } = useLanguage();

  const [buildingId, setBuildingIdState] = React.useState<string>(
    () => sessionStorage.getItem(SELECTED_BUILDING_KEY) || ""
  );
  const setBuildingId = React.useCallback((id: string) => {
    setBuildingIdState(id);
    if (id) sessionStorage.setItem(SELECTED_BUILDING_KEY, id);
    else sessionStorage.removeItem(SELECTED_BUILDING_KEY);
  }, []);
  const selectedBuildingValue = React.useMemo<SelectedBuildingState>(
    () => ({ buildingId, setBuildingId }),
    [buildingId, setBuildingId]
  );

  // `/buildings/` is already scoped to `hoa_admin = user`. Same query key the
  // issues and announcements pages use, so this shares their cache rather
  // than adding a request.
  const { data: myBuildings } = useQuery({
    queryKey: ["buildings", "mine"],
    queryFn: listBuildings,
    staleTime: 5 * 60 * 1000,
  });

  const adminName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
    user?.email ||
    "";

  /**
   * Which HOA the admin is looking at.
   *
   * One building means there is nothing to choose, so name it outright. More
   * than one falls back to "all" until they pick, matching the dashboard's
   * own selector. Empty string renders nothing rather than a stray bullet.
   */
  const buildingLabel = React.useMemo(() => {
    const buildings = myBuildings ?? [];
    if (buildings.length === 0) return "";
    if (buildings.length === 1) return buildings[0].name;
    const picked = buildings.find((b) => b.id === buildingId);
    return picked ? picked.name : t("common.allCondos");
  }, [myBuildings, buildingId, t]);
  
  // Enable dev shortcuts (Ctrl+Shift+L to open logs)
  useDevShortcuts();

  async function handleLogout() {
    try {
      // Call logout (backend + clear tokens + clear sessionStorage)
      await logout();
      
      // Clear sessionStorage (HOA admin uses sessionStorage, NOT localStorage)
      sessionStorage.clear();
      
      // Clear in-memory token
      clearAccessToken();
      
      // Force full page reload to clear browser cache and prevent race conditions
      window.location.href = "/#/login";
    } catch (error) {
      console.error("Logout failed:", error);
      
      // Force logout even if everything fails
      sessionStorage.clear();
      clearAccessToken();
      
      // Force reload
      window.location.href = "/#/login";
    }
  }

  return (
    <PollingContext.Provider value={pollingValue}>
      <SelectedBuildingContext.Provider value={selectedBuildingValue}>
      <div className="min-h-screen flex flex-col relative" style={{ backgroundColor: BG }}>
        {/* Ambient background */}
        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1000px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

        {/* Header */}
        <header className="sticky top-0 z-40">
          <div className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
            <div className="max-w-5xl mx-auto px-4 py-2.5">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1 space-y-0.5">
                  <h1 className="truncate text-[17px] font-semibold leading-tight tracking-[-0.01em]">
                  {titleFromPath(pathname, t)}
                </h1>
                {/* Who is signed in and which HOA they are looking at, the way
                    the resident header shows a name and a unit. It said
                    "Consola HOA · Administrador" -- two constants, identifying
                    neither the person nor the building.

                    The role is dropped below sm: on a 360px phone the line
                    competes with the bell and the logout button, and of the
                    three the role is the one the reader already knows. */}
                <div className="flex min-w-0 items-center gap-1.5 text-[13px] text-gray-500">
                  {adminName && <span className="truncate">{adminName}</span>}
                  {adminName && <span aria-hidden className="shrink-0 hidden sm:inline">•</span>}
                  <span className="shrink-0 hidden sm:inline">{t("roles.hoaAdmin")}</span>
                  {buildingLabel && (
                    <>
                      <span aria-hidden className="shrink-0">•</span>
                      <span className="truncate font-medium text-gray-600">{buildingLabel}</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <NotificationBell role="hoa-admin" />
                <Button
                  size="sm"
                  onClick={handleLogout}
                  title={t("common.logout")}
                  className="gap-2 rounded-xl bg-white text-gray-900 ring-1 ring-black/5 border-0 hover:bg-white active:bg-gray-50"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("common.logout")}</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
        </header>

        {/* Main */}
        <main className="flex-1">
          <div className="max-w-5xl mx-auto px-4 py-4 pb-28 sm:pb-32">
            <Outlet />
          </div>
        </main>

        {/* Floating bottom dock */}
        <nav className="fixed inset-x-0 bottom-0 z-40 pt-5 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {/* The dock floats clear of the bottom edge, so the page used to scroll
              through the 12px gap beneath it and past its rounded corners -- cards
              reappeared under the bar and it read as content escaping. The nav now
              spans to the bottom edge and paints the page colour behind itself,
              fading out above so content is not cut off with a hard line. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{ background: `linear-gradient(to top, ${BG} 0%, ${BG} 62%, transparent 100%)` }}
          />
          <div className="relative mx-auto max-w-5xl px-1 sm:px-2">
            {/* Issues had no tab because the page was behind a "Coming Soon"
                overlay; it was reachable only from a dashboard tile. Now that
                it is real, it belongs in the dock like everything else. */}
            <ul className="grid grid-cols-8 gap-0.5 sm:gap-1 rounded-2xl bg-white/80 backdrop-blur-xl ring-1 ring-black/5 shadow-lg shadow-black/10 p-1.5 sm:p-2">
              <Tab to="/hoa-admin" icon={<Home className="w-5 h-5" />} label={t("nav.home")} />
              <Tab to="/hoa-admin/members" icon={<Users className="w-5 h-5" />} label={t("nav.members")} />
              <Tab to="/hoa-admin/payments" icon={<FileText className="w-5 h-5" />} label={t("nav.invoice")} />
              <Tab to="/hoa-admin/issues" icon={<Wrench className="w-5 h-5" />} label={t("nav.issues")} />
              <Tab to="/hoa-admin/communications" icon={<Megaphone className="w-5 h-5" />} label={t("nav.comms")} />
              <Tab to="/hoa-admin/reports" icon={<BarChart3 className="w-5 h-5" />} label={t("nav.reports")} />
              <Tab to="/hoa-admin/documents" icon={<FolderOpen className="w-5 h-5" />} label={t("m3.documents.title")} />
            <Tab to="/hoa-admin/settings" icon={<Settings className="w-5 h-5" />} label={t("nav.settings")} />
            </ul>
          </div>
        </nav>
      </div>
      </SelectedBuildingContext.Provider>
    </PollingContext.Provider>
  );
}

function Tab({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    // min-w-0 is load-bearing: a grid item defaults to min-width:auto, so it
    // refuses to shrink below its content and the label spills across its
    // neighbours instead of being clipped. With seven tabs on a 360px phone a
    // cell is 43px wide and "Comunicaciones" renders at 90px.
    <li className="min-w-0">
      <NavLink
        to={to}
        end={to === "/hoa-admin"}
        className={[
          "group relative flex w-full min-w-0 flex-col items-center justify-center rounded-xl py-2 transition",
          "focus-visible:ring-2 focus-visible:ring-[color:var(--brand)]/30 outline-none",
        ].join(" ")}
      >
        {({ isActive }: { isActive: boolean }) => (
          <>
            <span
              className="grid place-items-center transition-colors"
              style={{ color: isActive ? PRIMARY : "#4b5563" }}
            >
              {icon}
            </span>
            {/* truncate is the backstop, not the plan -- the labels below are
                short enough to fit. It guarantees that a longer translation
                clips cleanly rather than overlapping the next tab. */}
            <span
              className="mt-1 w-full truncate text-center text-[10px] font-medium tracking-tight transition-colors sm:text-[11px]"
              style={{ color: isActive ? PRIMARY : "#4b5563" }}
              title={label}
            >
              {label}
            </span>
            <span
              aria-hidden
              className="absolute -bottom-0.5 h-1 w-6 rounded-full transition-opacity"
              style={{
                backgroundColor: PRIMARY,
                opacity: isActive ? 0.7 : 0,
              }}
            />
          </>
        )}
      </NavLink>
    </li>
  );
}

function titleFromPath(path: string, t: (key: string) => string) {
  if (path.includes("/members")) return t("hoa.layout.titles.members");
  if (path.includes("/payments")) return t("hoa.layout.titles.payments");
  if (path.includes("/communications")) return t("hoa.layout.titles.communications");
  if (path.includes("/reports")) return t("hoa.layout.titles.reports");
  if (path.includes("/documents")) return t("m3.documents.title");
  if (path.includes("/settings")) return t("nav.settings");
  return t("nav.dashboard");
}
